import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getContracts, favouriteContract, fmtValue } from "@/lib/api";
import { ScoreBadge } from "@/components/ScoreBadge";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { Button } from "@/components/ui/button";
import { Star, RadarIcon, Clock, ArrowRight, AlertTriangle, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";

const SOURCE_META = {
  "Find a Tender": { label: "Find a Tender", cls: "bg-[#2F6DA3] text-white" },
  "Public Contracts Scotland": { label: "Scotland · PCS", cls: "bg-[#14304F] text-white" },
  "Contracts Finder": { label: "Contracts Finder", cls: "bg-zinc-100 text-zinc-500 border border-zinc-300" },
};

export default function Favourites() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["contracts", "favourites-page"],
    queryFn: () => getContracts({ favourite: true, min_score: 1, sort: "score" }),
  });
  const contracts = data?.contracts || [];

  const unfavourite = async (e, c) => {
    e.stopPropagation();
    qc.setQueriesData({ queryKey: ["contracts"] }, (old) =>
      old ? { ...old, contracts: old.contracts.filter((x) => x.id !== c.id), count: old.count - 1 } : old
    );
    try {
      await favouriteContract(c.id, false);
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (err) {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      toast.error("Could not update favourite.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-500 mb-1">Your shortlist</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter flex items-center gap-3">
          <Star size={36} className="text-amber-400" fill="currentColor" /> Favourites
        </h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          The contracts you've starred — your team's shortlist of opportunities worth pursuing.
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-20 font-mono text-sm text-zinc-400">Loading…</div>
      ) : contracts.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 bg-white py-20 text-center" data-testid="fav-empty">
          <Star size={32} className="text-zinc-300 mx-auto mb-3" />
          <p className="font-display text-2xl font-bold mb-2">No favourites yet</p>
          <p className="text-sm text-zinc-500 mb-6">Star a contract on the Opportunities page to add it to your shortlist.</p>
          <Button onClick={() => navigate("/")} className="bg-zinc-950 text-white font-bold uppercase tracking-wider" data-testid="go-opportunities">
            Browse Opportunities
          </Button>
        </div>
      ) : (
        <>
          <p className="text-xs font-mono text-zinc-400" data-testid="fav-count">{contracts.length} favourite{contracts.length === 1 ? "" : "s"}</p>
          <div className="space-y-4">
            {contracts.map((c, i) => {
              const upcoming = c.stage === "upcoming";
              const sm = SOURCE_META[c.source] || SOURCE_META["Contracts Finder"];
              return (
                <div
                  key={c.id}
                  data-testid={`fav-card-${c.id}`}
                  onClick={() => navigate(`/contract/${c.id}`)}
                  className="group border-2 border-l-[6px] border-l-amber-400 border-amber-200 bg-white p-5 cursor-pointer fade-up hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_#fbbf24] transition-all duration-200"
                  style={{ animationDelay: `${i * 25}ms` }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <ScoreBadge score={c.ai_score} recommendation={c.ai_recommendation} />
                        {upcoming ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#2F6DA3] text-white px-2 py-1"><Clock size={11} /> Upcoming</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-1"><RadarIcon size={11} /> Live</span>
                        )}
                        <DeadlineBadge deadline={c.deadline} />
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${sm.cls}`}>{sm.label}</span>
                      </div>
                      <h3 className="font-display text-xl font-bold tracking-tight mb-1 group-hover:text-[#2F6DA3] transition-colors">{c.title}</h3>
                      <p className="text-sm text-zinc-500 line-clamp-2 mb-2">{c.ai_summary || c.description}</p>
                      {c.readiness_gaps && c.readiness_gaps.length > 0 && (
                        <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600" />
                          <span><strong>May need:</strong> {c.readiness_gaps.join(" · ")} <span className="text-amber-600">(check Readiness)</span></span>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400 font-mono mt-3">
                        {c.buyer_name && <span className="flex items-center gap-1"><Building2 size={12} /> {c.buyer_name}</span>}
                        {(c.region || c.country) && <span className="flex items-center gap-1"><MapPin size={12} /> {c.region || c.country}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end">
                      <button
                        data-testid={`fav-remove-${c.id}`}
                        onClick={(e) => unfavourite(e, c)}
                        title="Remove from favourites"
                        className="mb-2 p-1.5 text-amber-500 border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 transition-colors"
                      >
                        <Star size={16} fill="currentColor" strokeWidth={2.5} />
                      </button>
                      <div className="font-display text-2xl font-extrabold tracking-tight">{fmtValue(c.value_amount)}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Est. Value</div>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#2F6DA3]">Bid Writer <ArrowRight size={13} /></span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
